// ---------------------------------------------------------------------------------------------
// HOME-1 FINAL — **WHAT HER FRONT DOOR PROMISES, AND WHOSE HOME PAGE THIS IS.**
//
// >>> THE TAP MAP IS GONE FROM HERE, MIGRATED AT ITS DECLARATION RATHER THAN LEFT STANDING BESIDE
// ITS REPLACEMENT (DL-2/DT-2b/R181, R165). <<< This module was the map of what a thumb can touch on
// `HomeArtwork` — `ZONE_KINDS`, both zone rosters, `zoneDestination`, `zoneCenter`, `zoneLabel`,
// `zoneReading`, `zoneViolations`, `overlappingZones`, `artworkDrawsMenu`, `MIN_TAP_PX`,
// `CATEGORY_LINKS`. **LANDING v3 replaced that artwork and `src/landingTapMap.js` carries every one
// of those laws over the surface that really paints**, so what stood here was a second, unread
// spelling of them: a member could not reach it, no fixture drove it, and R165's census named six
// of its exports as reachable from nothing. **A retired law beside its replacement is one a later
// round finds and wires by reflex**, and on a tap map that means a door pointing at coordinates on
// a picture this site no longer serves.
//
// **WHAT SURVIVES IS THE HALF THAT WAS NEVER ABOUT THE ARTWORK (R18):** her CARD ROSTER, which is
// what her front door PROMISES and which `FunnelCapture`, `roomOrigin` and the mini rail all read;
// the one breakpoint two live surfaces still share; the two lines her page declares for a screen
// reader; and `strangerHomeFace`, which answers WHOSE home page this is rather than what is on it.
//
// **THIS MODULE IMPORTS NOTHING**, which is the entry requirement that lets a `.mjs` gate load and
// RUN it (R8/R95) — so the built roster is held against the SERVER'S own configs rather than
// against a receipt that agrees with itself.
// ---------------------------------------------------------------------------------------------

// >>> HER CONVERSION RULING, AND IT IS THE ONE THING NOTHING HERE MAY SOFTEN. <<< *"THE SIX READING
// CARDS: these are PAID doors, not free funnels. Each card lands on the SIGNUP page (pre-signup),
// and post-signup the membership popup fires when they reach the reading, the standard gate flow."*
//
// So a card's destination is `SIGNUP` on this page, full stop — this surface is only ever seen by
// somebody with no session. The reading each card PROMISES is carried beside it as `reading`, which
// is what a member is taken to once she has an account, and what the fixture holds against the real
// roster. Keeping the promise next to the zone is what stops the two drifting: a card whose art says
// one thing and whose door opens another is the defect this pairing makes visible.
//
// >>> AND HER CORRECTION MADE THE DESTINATION A DOOR RATHER THAN A PAGE, WHICH IS NOT A DETAIL.
// <<< Her clarified ruling: *"the six reading cards, Get My Answer, and Explore the House all land
// on CREATE ACCOUNT (the signup form), NEVER a payment popup first. They sign up (captured
// forever), land toward the reading they wanted, and the membership popup meets them at the reading
// itself. DESIRE, ACCOUNT, THEN PRICE."*
//
// **MEASURED (R25/R180): `membership` RENDERS THE TIER LADDER WITH PRICES ON IT** -- `MembershipPage`
// reads `readTierCatalog` and draws the cards and their costs. So the first render sent every one of
// her paid doors straight at a price, which is her *never a payment popup first* broken by exactly
// one roster value, silently, on the page her advertising pays for. The create-account door is
// `SignInModal`, which takes a NAME and an ADDRESS -- *captured forever*, in her own words.
export const SIGNUP = "signup";

// **HER SIX CARDS, IN HER OWN ORDER AND HER OWN WORDS.** The `promise` is the text painted into her
// artwork, quoted so the fixture can hold a card's destination against what the card SAYS — her
// clause 3's *"titled exactly as the cards promise"* is checkable only if the promise is written
// down. `reading` is the post-signup destination; `built` records whether that reading exists yet,
// because two of the six are new builds and four were measured as NOT existing as member readings.
// >>> HER DESKTOP ART CHANGED AND HER VERTICAL DID NOT, SO THE TWO VIEWPORTS ARE NOW TWO DESIGNS
// WITH TWO DIFFERENT SETS OF PROMISES. <<<
//
// Her correction of 2026-08-18 is explicit: *"the VERTICAL/mobile asset is KEPT (she prefers the
// current one, do not touch it), ONLY the desktop artwork is being replaced."* PROVED BY HASH before
// a line was written -- `new background-vertical.png` is byte-identical to what HOME-1 shipped
// (`9bb8254df21b9a5e`) and `new background.png` is not (`e17b5a75d07f81d0` -> `d75654df224dbf3a`).
//
// So a SINGLE `READING_CARDS` is no longer true of this house: the phone still asks her six questions
// and the desktop asks five different ones. One roster stretched across both would put desktop
// promises on a phone that does not make them, which is the dead tap this whole file exists to
// refuse -- so there are two rosters and a UNION, and the union is what My Room doors (her
// guarantee: *nobody pays and then cannot find the thing that brought them*, whichever screen
// brought them).

/** The six her PHONE art asks, unchanged since HOME-1 because that artwork is unchanged. */
export const PHONE_CARDS = Object.freeze([
  { id: "really-feel", promise: "How do they really feel about you?", reading: "how-they-really-feel" },
  { id: "blocking-money", promise: "What's blocking my money?", reading: "whats-blocking-my-money" },
  { id: "psychic", promise: "How psychic are you?", reading: "how-psychic-are-you" },
  { id: "wealthy", promise: "Were you born to be wealthy?", reading: "born-to-be-wealthy" },
  { id: "when-money", promise: "When is money coming?", reading: "when-is-money-coming" },
  { id: "manifestation", promise: "What's my manifestation type?", reading: "manifestation-type" },
]);

/**
 * The five her NEW DESKTOP art asks. Two of them are the same READING under different words -- which
 * is fine and is why a card carries both: the PROMISE is what she wrote on the artwork and the
 * READING is what the house delivers. Quoting the promise from the wrong viewport's roster is the
 * paraphrase `MyRoomReadingDoors` refuses.
 */
export const DESKTOP_CARDS = Object.freeze([
  { id: "d-really-feel", promise: "How do they really feel about me?", reading: "how-they-really-feel" },
  { id: "d-money-change", promise: "Will my money change soon?", reading: "when-is-money-coming" },
  { id: "d-life-purpose", promise: "What is my life purpose?", reading: "what-is-my-life-purpose" },
  { id: "d-whats-coming", promise: "What's coming next for me?", reading: "whats-coming-next-for-me" },
  { id: "d-hidden-gifts", promise: "What are my hidden gifts and abilities?", reading: "spiritual-gift" },
]);

/**
 * >>> EVERY PROMISE EITHER SCREEN MAKES, WHICH IS WHAT MY ROOM DOORS. <<<
 *
 * Deduped on the READING, because two cards asking the same question in two wordings are ONE door in
 * her room -- and the promise kept is the one the DESKTOP makes, because that is the newer artwork
 * and the one her advertising is being pointed at. `READING_CARDS` keeps its name so nothing that
 * reads it has to know the shape changed underneath.
 */
export const READING_CARDS = Object.freeze(
  [...DESKTOP_CARDS, ...PHONE_CARDS].filter(
    (c, i, all) => all.findIndex((o) => o.reading === c.reading) === i,
  ),
);

// **ONE BREAKPOINT, AND IT OUTLIVED THE ARTWORK IT WAS MEASURED FOR.** It was the number the
// `<picture>` media query and the zone map shared, so that a layout could never be offered at one
// width and mapped at another. Both of those are gone with `HomeArtwork` — and the constant is
// still READ by her signup page and her membership page, which is why it is here rather than swept
// with them (R19: a change with no defect under it is its own defect).
export const HOME_ART_BREAKPOINT = 820;

// ---------------------------------------------------------------------------------------------
// **WHAT HER ARTWORK SAYS, WRITTEN DOWN SO A SCREEN READER AND A GATE CAN BOTH READ IT.**
//
// Her design paints these words; pixels say nothing to a screen reader, to a search engine, or to
// ITEM 14b's census. So the headline is DECLARED here and rendered `.visually-hidden` as the page's
// `<h1>` — R43's own repair pattern: the outline is honest and NOTHING ON SCREEN CHANGES.
//
// **IT IS QUOTED FROM HER FILE, NOT COMPOSED.** A heading that says something other than what the
// picture says is worse than none, because it is what a reader who cannot see the picture is told
// the page is about.
export const HOME_HERO_LINE = "Curious about love, money, or what's next?";
export const HOME_SUB_LINE =
  "12th House brings your readings, chart, intuition, and spiritual questions together in one place.";

// ---------------------------------------------------------------------------------------------
// **WHAT IS BEHIND EACH CARD, MEASURED RATHER THAN ASSUMED (R25/R180) AND PRINTED RATHER THAN
// PAPERED OVER (R17/R160).**
//
// >>> HER CLAUSE 3 NAMES TWO CARDS AS NEW BUILDS AND READS AS THOUGH THE OTHER FOUR ALREADY EXIST.
// MEASURED AGAINST THE ROSTERS: THEY DO NOT. <<< `mini-reports.json` holds spiritual-gift,
// hidden-enemies, ideal-job, meant-to-marry and drawn-to-you; `astrology-reports.json` holds the
// nine deep reports; `FUNNEL_READINGS` holds wealthy, psychic and timeline — which are FREE FUNNEL
// doors, and her clause 2(f) rules explicitly that the cards are **not** those.
//
// **THE TAP MAP IS CORRECT EITHER WAY AND SHIPS TODAY**, because every card lands on SIGNUP
// pre-signup, which is the whole of what a stranger meets. What is owed is the surface a member
// reaches AFTER she pays, and that debt is recorded HERE rather than left for somebody to discover
// by tapping — a promise on her page with nothing behind it is the dead tap her clause 4 is about,
// one screen further in.
// >>> THE DEBT ABOVE IS PAID (2026-08-17, her overnight item 2), AND THE NOTE IS MIGRATED WHERE IT
// STOOD RATHER THAN LEFT STANDING (R68: a declared debt that no longer applies reads as a gap). <<<
// Her word was that a person who clicked a card, signed up and PAID must find exactly what she came
// for, so the four `unbuiltCards()` named were built onto the mini rail: `how-they-really-feel`,
// `whats-blocking-my-money`, `how-psychic-are-you` and `born-to-be-wealthy`. ALL SIX now have a
// member reading behind them.
//
// THIS ROSTER IS A RECEIPT AND RECEIPTS ROT, so it is held as a BICONDITIONAL against
// `server/data/mini-reports.json` by the fixture rather than trusted (R11/R31/R141): a card marked
// built with no config behind it is a promise on her page with nothing under it, and a card with a
// config that is NOT marked built is a debt somebody already paid and is still declaring. It stays a
// LITERAL here rather than a derivation because this module imports NOTHING, which is the entry
// requirement that lets a `.mjs` gate load and RUN it -- reading a server JSON here would buy the
// derivation and pay for it with the property the whole file exists for.
export const READING_CARDS_BUILT = Object.freeze([
  "when-is-money-coming",
  "manifestation-type",
  "how-they-really-feel",
  "whats-blocking-my-money",
  "how-psychic-are-you",
  "born-to-be-wealthy",
  // Her NEW desktop art's three (2026-08-18). `spiritual-gift` was ALREADY on the mini rail and
  // needed no build -- her card *"What are my hidden gifts and abilities?"* is that reading under her
  // own words, which is exactly why a card carries a PROMISE and a READING separately.
  "what-is-my-life-purpose",
  "whats-coming-next-for-me",
  "spiritual-gift",
]);

/** The card promises with no member reading behind them yet. Her four, by name. */
export function unbuiltCards() {
  return READING_CARDS.filter((c) => !READING_CARDS_BUILT.includes(c.reading)).map((c) => c.id);
}

// >>> WHOSE HOME PAGE IS THIS, AND *WE HAVE NOT LOOKED YET* IS NOT *SHE IS A STRANGER* (R18/L5.1).
// <<<
// `isLoggedIn` is FALSE until `restoreSession` answers, so a member landing on her own bookmark is
// signed out for a beat -- and on THIS page that beat hides the whole site chrome, because the
// stranger home paints her artwork edge to edge and `body.is-stranger-home` takes the header, the
// sidebar and the footer off the screen. Measured: the FAST gate's Ask Lala checks navigate to `/`
// and click "Ask Lala" IN THE SITE MENU, and they went red the day this page shipped -- the menu was
// not there to click. That is B2's own ruling one room over (`roomEntryFace` asks
// `isLoggedIn === false AND sessionKnown === true`), and it is the same defect L5.1 recorded: a
// paying Apprentice landing on her own bookmark met the membership box for a beat.
//
// THE DIRECTION IS THE RULING (R19/R8-4). Drawing the ORDINARY home for a stranger for one beat is
// FREE-1's browse-freely page and is harmless -- she sees the house, then she sees the artwork.
// Drawing the ARTWORK at a member takes her navigation away with nothing on screen to say so, which
// is silent and is what the gate caught. So every uncertainty answers `house`.
export function strangerHomeFace({ sessionKnown = false, isLoggedIn = false } = {}) {
  if (sessionKnown !== true) return "house";
  return isLoggedIn === true ? "house" : "stranger";
}

// The two call sites -- the page itself and `siteFooterHidden` -- ask ONE question and must never
// hold two opinions about it (R28/R29): a footer drawn over her full-bleed artwork is the chrome
// this page exists to remove, and a footer hidden on a member's ordinary home is a member with no
// way out of the bottom of the page.
export function showsStrangerHome(state) {
  return strangerHomeFace(state) === "stranger";
}
